
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import type { ProductType as Product } from '../../utils/supabase';

export default function ProductoDetalle() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Función para verificar si hay sesión válida
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return false;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return false;
      }

      return true;
    } catch (error) {
      localStorage.removeItem('user_session');
      return false;
    }
  };

  // Cargar producto
  const loadProduct = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .eq('active', true)
        .single();
      
      if (error) throw error;
      setProduct(data);
    } catch (err) {
      console.error('Error loading product', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsLoggedIn(checkSessionValidity());
    loadProduct();
  }, [id]);

  const handleAdminClick = () => {
    if (isLoggedIn) {
      window.REACT_APP_NAVIGATE('/admin');
    } else {
      window.REACT_APP_NAVIGATE('/login');
    }
  };

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('es-MX').format(num);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin mb-4"></i>
          <p className="text-gray-600">Cargando producto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Producto no encontrado</h2>
          <p className="text-gray-600 mb-6">El producto que buscas no existe o no está disponible</p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Volver a la Tienda
          </button>
        </div>
      </div>
    );
  }

  const images = [product.image_url, product.image_url_2, product.image_url_3, product.image_url_4].filter(Boolean);
  const defaultImage = 'https://readdy.ai/api/search-image?query=Pet%20product%20merchandise%20for%20animal%20shelter%20fundraising%2C%20cute%20dog%20accessories%20and%20toys%2C%20heartwarming%20pet%20store%20items%20with%20simple%20background&width=600&height=600&seq=product-detail&orientation=squarish';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div 
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => window.REACT_APP_NAVIGATE('/')}
            >
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                  Huellitas Unidas
                </h1>
                <p className="text-sm text-gray-600">Salvando vidas, una patita a la vez</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-2 bg-gray-100 rounded-2xl p-1">
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const event = new CustomEvent('tabChange', { detail: 'donar' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-orange-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-heart-fill"></i>
                <span>Donar</span>
              </button>
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const event = new CustomEvent('tabChange', { detail: 'adoptar' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-blue-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-heart-fill"></i>
                <span>Adoptar</span>
              </button>
              <button
                onClick={() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const event = new CustomEvent('tabChange', { detail: 'refugios' });
                    window.dispatchEvent(event);
                  }, 100);
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-green-5 00 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-building-fill"></i>
                <span>Refugios</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/mision-huellitas')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-fill"></i>
                <span>Misión Huellitas</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 bg-orange-500 text-white shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-store-fill"></i>
                <span>Tienda</span>
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/register')}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-purple-500 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-fill"></i>
                <span>Registrarse</span>
              </button>
              <button
                onClick={handleAdminClick}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 text-gray-600 hover:text-gray-800 hover:bg-white whitespace-nowrap cursor-pointer"
              >
                <span>Iniciar sesión</span>
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm text-gray-600">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="hover:text-orange-500 transition-colors cursor-pointer"
            >
              Inicio
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
              className="hover:text-orange-500 transition-colors cursor-pointer"
            >
              Tienda
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-800 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden">
                <img
                  src={images[selectedImage] || defaultImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        selectedImage === index
                          ? 'border-orange-500 ring-2 ring-orange-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    <i className="ri-check-line mr-1"></i>
                    Disponible
                  </span>
                  <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
                    <i className="ri-heart-fill mr-1"></i>
                    Ayuda a los peluditos
                  </span>
                </div>
                
                <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
                
                <div className="flex items-baseline space-x-2 mb-6">
                  <span className="text-5xl font-bold text-green-600">
                    ${formatNumber(product.price)}
                  </span>
                  <span className="text-xl text-gray-500">MXN</span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-blue-50 p-6 rounded-2xl">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <i className="ri-heart-fill text-white text-lg"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Impacto de tu compra</h3>
                </div>
                <p className="text-gray-700">
                  El 100% de los fondos recaudados se destina a fortalecer este proyecto y brindar apoyo integral a perritos en situación de abandono.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Descripción</h3>
                <p className="text-gray-700 leading-relaxed">{product.description}</p>
              </div>

              {/* Quantity Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cantidad
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="text-xl font-semibold text-gray-900 min-w-[3rem] text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Total Price */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-700">Total:</span>
                  <span className="text-3xl font-bold text-green-600">
                    ${formatNumber(product.price * quantity)}
                  </span>
                </div>
              </div>

              {/* Buy Button */}
              <button
                onClick={() => {
                  alert(`¡Gracias por tu interés en ${product.name}! Pronto habilitaremos el sistema de pagos. Mientras tanto, puedes hacer una donación directa.`);
                }}
                className="w-full bg-gradient-to-r from-orange-500 to-blue-500 text-white py-4 px-6 rounded-2xl font-bold text-xl hover:from-orange-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-fill mr-3"></i>
                Comprar Ahora
              </button>

              {/* Additional Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-green-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Calidad garantizada</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-truck-line text-blue-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Envío a todo México</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <i className="ri-heart-3-line text-purple-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Ayudas a los peluditos</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-orange-600"></i>
                  </div>
                  <span className="text-sm text-gray-600">Soporte 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products / Call to Action */}
      <section className="py-16 bg-gradient-to-r from-orange-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">¡Gracias por ayudar!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Con tu compra estás contribuyendo directamente al bienestar de los perritos rescatados
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/tienda')}
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-store-line mr-2"></i>
              Ver Más Productos
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/mision-huellitas')}
              className="bg-white/20 hover:bg-white/30 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-rocket-fill mr-2"></i>
              Ver Misiones
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
